import React, { useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlay } from "@fortawesome/free-solid-svg-icons"
import { URL } from "../../../store/actions/depannageActions"
import { COLORS } from "../../../utilities/Constants"

// GE-62 (B4) : photos d'une mission pas encore convertie, depuis le détail
// En cours — carte grise en premier, puis avant / après ; clic = agrandir.
// Les fichiers viennent du sérialiseur de la mission (`photos`, `videos`).

const FILE_TYPE_AVANT = 0
const FILE_TYPE_APRES = 1
export const FILE_TYPE_CARTE_GRISE = 2
const ORDRE = [FILE_TYPE_CARTE_GRISE, FILE_TYPE_AVANT, FILE_TYPE_APRES]
const LIBELLES = {
	[FILE_TYPE_CARTE_GRISE]: "Carte grise",
	[FILE_TYPE_AVANT]: "Avant",
	[FILE_TYPE_APRES]: "Après",
}
const VIDEO_EXTS = ["mp4", "mov", "m4v", "webm"]

export const urlMedia = (url) => {
	if (!url) return ""
	const base = URL.endsWith("/") ? URL.slice(0, -1) : URL
	return `${base}${url.startsWith("/") ? url : `/${url}`}`
}

const estVideo = (f) =>
	VIDEO_EXTS.includes((f.name || "").split(".").pop().toLowerCase())

// Carte grise, avant, après (ordre de prise conservé dans chaque groupe),
// puis les vidéos.
export const fichiersMission = (dm) => {
	const photos = [...((dm && dm.photos) || [])].sort(
		(a, b) =>
			ORDRE.indexOf(a.fileType) - ORDRE.indexOf(b.fileType) ||
			a.id - b.id,
	)
	return [...photos, ...((dm && dm.videos) || [])]
}

export const carteGrise = (dm) =>
	((dm && dm.photos) || []).find((f) => f.fileType === FILE_TYPE_CARTE_GRISE)

const libelle = (f) => (estVideo(f) ? "Vidéo" : LIBELLES[f.fileType] || "Photo")

// Vignette cliquable (image, ou tuile vidéo).
export const Vignette = ({
	file,
	onClick,
	largeur = 84,
	hauteur = 64,
	actif = false,
}) => (
	<button
		type="button"
		onClick={onClick}
		style={{ ...st.vignette, width: largeur }}
		title={`${libelle(file)} — agrandir`}
		aria-label={`${libelle(file)} — agrandir`}
	>
		<span
			style={{
				...st.cadre,
				height: hauteur,
				...(actif ? { border: `2px solid ${COLORS.accent}` } : null),
			}}
		>
			{estVideo(file) ? (
				<FontAwesomeIcon
					icon={faPlay}
					style={{ color: COLORS.accent, fontSize: 18 }}
				/>
			) : (
				<img
					src={urlMedia(file.url)}
					alt=""
					style={st.image}
					loading="lazy"
				/>
			)}
		</span>
		<span style={st.legende}>{libelle(file)}</span>
	</button>
)

// Agrandissement plein écran, ←/→ entre les fichiers, Échap ferme. Portail
// vers <body>, au-dessus du CustomModal du détail.
export const Visionneuse = ({ fichiers, index, setIndex, onClose }) => {
	const file = fichiers[index]
	const aller = (d) =>
		setIndex((index + d + fichiers.length) % fichiers.length)
	useEffect(() => {
		const touche = (e) => {
			if (e.key === "Escape") onClose()
			if (fichiers.length < 2) return
			if (e.key === "ArrowLeft") aller(-1)
			if (e.key === "ArrowRight") aller(1)
		}
		window.addEventListener("keydown", touche)
		return () => window.removeEventListener("keydown", touche)
	})
	if (!file) return null
	return ReactDOM.createPortal(
		<div style={st.fond} onClick={onClose}>
			<div style={st.barre} onClick={(e) => e.stopPropagation()}>
				<span style={{ fontWeight: 600 }}>{libelle(file)}</span>
				{fichiers.length > 1 && (
					<span style={{ opacity: 0.7, fontSize: 12.5 }}>
						{`${index + 1} / ${fichiers.length}`}
					</span>
				)}
				<span style={{ flex: 1 }} />
				{fichiers.length > 1 && (
					<>
						<button
							type="button"
							style={st.bouton}
							onClick={() => aller(-1)}
							aria-label="Précédente"
						>
							←
						</button>
						<button
							type="button"
							style={st.bouton}
							onClick={() => aller(1)}
							aria-label="Suivante"
						>
							→
						</button>
					</>
				)}
				<a
					href={urlMedia(file.url)}
					target="_blank"
					rel="noopener noreferrer"
					style={{ ...st.bouton, textDecoration: "none" }}
				>
					Ouvrir
				</a>
				<button
					type="button"
					style={st.bouton}
					onClick={onClose}
					aria-label="Fermer"
				>
					✕
				</button>
			</div>
			<div style={st.scene}>
				{estVideo(file) ? (
					<video
						src={urlMedia(file.url)}
						controls
						style={st.media}
						onClick={(e) => e.stopPropagation()}
					/>
				) : (
					<img
						src={urlMedia(file.url)}
						alt={libelle(file)}
						style={st.media}
						onClick={(e) => e.stopPropagation()}
					/>
				)}
			</div>
		</div>,
		document.body,
	)
}

// GE-62 (B5, Jordan 09.10) : slider de TOUTES les photos de la mission, à
// côté des champs de « Compléter » — lire la carte grise en saisissant.
// Pivoter (↺ / ↻ par 90°, photo prise de travers), zoomer (molette, boutons)
// et déplacer (glisser). Rotation / zoom locaux à l'écran, NON enregistrés :
// l'état vit chez le parent (`vues`, `index`) pour survivre à une bascule
// « Champs / Photos ».
const VUE_NEUTRE = { rot: 0, zoom: 1, x: 0, y: 0 }
const ZOOM_MAX = 6

export const SliderPhotos = ({ fichiers, index, setIndex, vues, setVues }) => {
	const sceneRef = useRef(null)
	const imgRef = useRef(null)
	const glisse = useRef(null)
	const aGlisse = useRef(false)
	// À 90° / 270°, l'image affichée (ajustée au cadre à plat) est remise à
	// l'échelle pour tenir dans le cadre une fois pivotée.
	const [ajuste, setAjuste] = useState(1)
	const mesurer = () => {
		const img = imgRef.current
		const scene = sceneRef.current
		if (!img || !scene || !img.offsetWidth || !img.offsetHeight) return
		setAjuste(
			Math.min(
				1,
				scene.clientWidth / img.offsetHeight,
				scene.clientHeight / img.offsetWidth,
			),
		)
	}
	const file = fichiers[Math.min(index, fichiers.length - 1)]
	const vue = (file && vues[file.id]) || VUE_NEUTRE
	const majVue = (f) =>
		setVues((avant) => ({
			...avant,
			[file.id]: f(avant[file.id] || VUE_NEUTRE),
		}))
	const zoomer = (facteur) =>
		majVue((v) => {
			const zoom = Math.min(ZOOM_MAX, Math.max(1, v.zoom * facteur))
			return zoom === 1 ? { ...v, zoom, x: 0, y: 0 } : { ...v, zoom }
		})

	// Mesure du cadre : changement de photo, de rotation, redimensionnement.
	useEffect(() => {
		mesurer()
		window.addEventListener("resize", mesurer)
		return () => window.removeEventListener("resize", mesurer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [index, vue.rot])

	// Décision 6 (revue phase B) : tactile — pincer pour zoomer, glisser
	// (à un doigt) pour déplacer une photo zoomée. Listeners non passifs :
	// la page ne défile pas sous les doigts.
	// Revue 5 (🟠3) : listeners posés UNE fois (le geste vit dans une ref) —
	// relancés à chaque rendu (chaque pas du geste re-rend le parent), ils
	// perdaient le geste après un pas et la page zoomait à la place.
	const vueRef = useRef(vue)
	vueRef.current = vue
	const majVueRef = useRef(majVue)
	majVueRef.current = majVue
	const zoomerRef = useRef(zoomer)
	zoomerRef.current = zoomer
	const geste = useRef(null)
	const avecPhoto = !!file
	useEffect(() => {
		const el = sceneRef.current
		if (!el) return
		const ecart = (t) =>
			Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY)
		const debutTouch = (e) => {
			const v = vueRef.current
			if (e.touches.length === 2) {
				geste.current = { pince: ecart(e.touches), zoom: v.zoom }
			} else if (e.touches.length === 1 && v.zoom > 1) {
				geste.current = {
					x: e.touches[0].clientX - v.x,
					y: e.touches[0].clientY - v.y,
				}
			}
		}
		const bougeTouch = (e) => {
			const g = geste.current
			if (!g) return
			e.preventDefault()
			if (g.pince && e.touches.length === 2) {
				const zoom = Math.min(
					ZOOM_MAX,
					Math.max(1, (g.zoom * ecart(e.touches)) / g.pince),
				)
				majVueRef.current((v) =>
					zoom === 1 ? { ...v, zoom, x: 0, y: 0 } : { ...v, zoom },
				)
			} else if (!g.pince && e.touches.length === 1) {
				const x = e.touches[0].clientX - g.x
				const y = e.touches[0].clientY - g.y
				majVueRef.current((v) => ({ ...v, x, y }))
			}
		}
		const finTouch = (e) => {
			// 2 → 1 doigt : on ne glisse pas avec le repère du pincement.
			if (
				e.touches.length === 0 ||
				(e.touches.length === 1 && geste.current && geste.current.pince)
			)
				geste.current = null
		}
		// Molette : listener non passif (sinon la page défile au lieu de
		// zoomer).
		const roue = (e) => {
			e.preventDefault()
			zoomerRef.current(e.deltaY < 0 ? 1.15 : 1 / 1.15)
		}
		el.addEventListener("touchstart", debutTouch, { passive: true })
		el.addEventListener("touchmove", bougeTouch, { passive: false })
		el.addEventListener("touchend", finTouch)
		// Appel système, notification, geste de l'OS.
		el.addEventListener("touchcancel", finTouch)
		el.addEventListener("wheel", roue, { passive: false })
		return () => {
			el.removeEventListener("touchstart", debutTouch)
			el.removeEventListener("touchmove", bougeTouch)
			el.removeEventListener("touchend", finTouch)
			el.removeEventListener("touchcancel", finTouch)
			el.removeEventListener("wheel", roue)
		}
	}, [avecPhoto])

	if (!file)
		return <div style={st.sliderVide}>Aucune photo pour l'instant</div>
	const aller = (d) =>
		setIndex((index + d + fichiers.length) % fichiers.length)
	const debut = (e) => {
		aGlisse.current = false
		if (vue.zoom <= 1) return
		e.preventDefault()
		glisse.current = {
			x: e.clientX - vue.x,
			y: e.clientY - vue.y,
			sx: e.clientX,
			sy: e.clientY,
		}
	}
	const bouge = (e) => {
		if (!glisse.current) return
		// Lu tout de suite : React 16 recycle l'événement (pooling).
		const x = e.clientX - glisse.current.x
		const y = e.clientY - glisse.current.y
		if (
			Math.abs(e.clientX - glisse.current.sx) +
				Math.abs(e.clientY - glisse.current.sy) >
			4
		)
			aGlisse.current = true
		majVue((v) => ({ ...v, x, y }))
	}
	// Décision 6 : clic SIMPLE = zoomer (×2,5) ; sur une photo zoomée, clic
	// sans glisser = revenir à la vue entière.
	const clic = () => {
		if (aGlisse.current) {
			aGlisse.current = false
			return
		}
		zoomer(vue.zoom > 1 ? 1 / vue.zoom : 2.5)
	}
	const fin = () => {
		glisse.current = null
	}
	const deTravers = vue.rot % 180 !== 0
	const echelle = vue.zoom * (deTravers ? ajuste : 1)
	const transform = `translate(${vue.x}px, ${vue.y}px) rotate(${vue.rot}deg) scale(${echelle})`
	const media = estVideo(file) ? (
		<video
			src={urlMedia(file.url)}
			controls
			style={{ ...st.sliderMedia, transform }}
		/>
	) : (
		<img
			ref={imgRef}
			src={urlMedia(file.url)}
			alt={libelle(file)}
			draggable={false}
			onLoad={mesurer}
			style={{
				...st.sliderMedia,
				transform,
				transition: glisse.current ? "none" : st.sliderMedia.transition,
				cursor: vue.zoom > 1 ? "grab" : "zoom-in",
			}}
			onClick={clic}
		/>
	)
	return (
		<div style={st.slider}>
			<div style={st.sliderBarre}>
				<span style={{ fontWeight: 600, fontSize: 13 }}>
					{libelle(file)}
				</span>
				<span style={{ fontSize: 12, color: COLORS.textMuted }}>
					{fichiers.length > 1
						? `${Math.min(index, fichiers.length - 1) + 1} / ${fichiers.length}`
						: ""}
				</span>
				<span style={{ flex: 1 }} />
				<button
					type="button"
					style={st.outil}
					onClick={() =>
						majVue((v) => ({ ...v, rot: (v.rot + 270) % 360 }))
					}
					title="Pivoter à gauche"
					aria-label="Pivoter à gauche"
				>
					↺
				</button>
				<button
					type="button"
					style={st.outil}
					onClick={() =>
						majVue((v) => ({ ...v, rot: (v.rot + 90) % 360 }))
					}
					title="Pivoter à droite"
					aria-label="Pivoter à droite"
				>
					↻
				</button>
				<button
					type="button"
					style={st.outil}
					onClick={() => zoomer(1 / 1.5)}
					disabled={vue.zoom <= 1}
					title="Dézoomer"
					aria-label="Dézoomer"
				>
					−
				</button>
				<button
					type="button"
					style={st.outil}
					onClick={() => zoomer(1.5)}
					disabled={vue.zoom >= ZOOM_MAX}
					title="Zoomer (ou clic, molette, pincement)"
					aria-label="Zoomer"
				>
					+
				</button>
				<a
					href={urlMedia(file.url)}
					target="_blank"
					rel="noopener noreferrer"
					style={{ ...st.outil, textDecoration: "none" }}
					title="Ouvrir l'original dans un onglet"
				>
					↗
				</a>
			</div>
			<div
				ref={sceneRef}
				// Revue 5 (🟠3) : sans zoom, la page défile sous les doigts ;
				// zoomée, la scène capte tout le geste.
				style={{
					...st.sliderScene,
					touchAction: vue.zoom > 1 ? "none" : "pan-x pan-y",
				}}
				onMouseDown={debut}
				onMouseMove={bouge}
				onMouseUp={fin}
				onMouseLeave={fin}
				data-scene-photo=""
			>
				{media}
				{fichiers.length > 1 && (
					<>
						<button
							type="button"
							style={{ ...st.fleche, left: 8 }}
							onClick={() => aller(-1)}
							aria-label="Photo précédente"
						>
							‹
						</button>
						<button
							type="button"
							style={{ ...st.fleche, right: 8 }}
							onClick={() => aller(1)}
							aria-label="Photo suivante"
						>
							›
						</button>
					</>
				)}
			</div>
			{fichiers.length > 1 && (
				<div style={st.sliderMiniatures}>
					{fichiers.map((f, i) => (
						<Vignette
							key={`mini_${f.id}`}
							file={f}
							largeur={64}
							hauteur={48}
							onClick={() => setIndex(i)}
							actif={i === index}
						/>
					))}
				</div>
			)}
		</div>
	)
}

// Bloc « Photos » du détail : vignettes + visionneuse. Rien sans fichier.
const PhotosMission = ({ dm, titre = "Photos" }) => {
	// Revue 5 (🟡) : photo ouverte suivie par ID (une photo arrivée en
	// cours de route se trie sans changer celle affichée), comme Compléter.
	const [ouvertId, setOuvertId] = useState(null)
	const fichiers = fichiersMission(dm)
	const indexOuvert = fichiers.findIndex((f) => f.id === ouvertId)
	if (fichiers.length === 0) return null
	return (
		<div style={{ marginBottom: 12 }}>
			<div style={st.titre}>{titre}</div>
			<div style={st.grille}>
				{fichiers.map((f, i) => (
					<Vignette
						key={`photo_${f.id}`}
						file={f}
						onClick={() => setOuvertId(f.id)}
					/>
				))}
			</div>
			{ouvertId !== null && indexOuvert >= 0 && (
				<Visionneuse
					fichiers={fichiers}
					index={indexOuvert}
					setIndex={(i) => fichiers[i] && setOuvertId(fichiers[i].id)}
					onClose={() => setOuvertId(null)}
				/>
			)}
		</div>
	)
}

const st = {
	titre: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		marginBottom: 6,
	},
	grille: { display: "flex", flexWrap: "wrap", gap: 8 },
	vignette: {
		display: "flex",
		flexDirection: "column",
		gap: 3,
		padding: 0,
		border: "none",
		background: "none",
		cursor: "zoom-in",
		textAlign: "left",
	},
	cadre: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
		borderRadius: 6,
		overflow: "hidden",
		border: `1px solid ${COLORS.border}`,
		background: COLORS.bg,
	},
	image: {
		width: "100%",
		height: "100%",
		objectFit: "cover",
		display: "block",
	},
	legende: { fontSize: 11, color: COLORS.textMuted },
	fond: {
		position: "fixed",
		inset: 0,
		zIndex: 3000,
		background: "rgba(28, 25, 23, 0.88)",
		display: "flex",
		flexDirection: "column",
	},
	barre: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "10px 16px",
		color: "#fff",
		fontSize: 13.5,
	},
	bouton: {
		background: "rgba(255,255,255,0.12)",
		border: "1px solid rgba(255,255,255,0.25)",
		color: "#fff",
		borderRadius: 6,
		padding: "4px 10px",
		cursor: "pointer",
		fontSize: 13,
	},
	scene: {
		flex: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "0 16px 16px",
		minHeight: 0,
	},
	slider: {
		display: "flex",
		flexDirection: "column",
		gap: 8,
		height: "100%",
		minHeight: 0,
	},
	sliderBarre: { display: "flex", alignItems: "center", gap: 6 },
	outil: {
		minWidth: 30,
		height: 30,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		background: COLORS.surface,
		color: COLORS.text,
		fontSize: 15,
		cursor: "pointer",
		fontFamily: "inherit",
	},
	sliderScene: {
		position: "relative",
		flex: 1,
		minHeight: 280,
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		background: "#1C1917",
		overflow: "hidden",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		userSelect: "none",
	},
	sliderMedia: {
		maxWidth: "100%",
		maxHeight: "100%",
		objectFit: "contain",
		transition: "transform 0.12s ease-out",
		transformOrigin: "center center",
	},
	fleche: {
		position: "absolute",
		top: "50%",
		transform: "translateY(-50%)",
		width: 32,
		height: 32,
		borderRadius: 16,
		border: "none",
		background: "rgba(255,255,255,0.85)",
		color: COLORS.text,
		fontSize: 20,
		lineHeight: "30px",
		cursor: "pointer",
	},
	sliderMiniatures: { display: "flex", gap: 6, overflowX: "auto" },
	sliderVide: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		minHeight: 200,
		borderRadius: 10,
		border: `1px dashed ${COLORS.borderStrong}`,
		fontSize: 13,
		color: COLORS.textLight,
	},
	media: {
		maxWidth: "100%",
		maxHeight: "100%",
		objectFit: "contain",
		borderRadius: 4,
	},
}

export default PhotosMission
